import { ArrowRight, CalendarClock, CheckCircle2, FileSignature, ShoppingBag, StickyNote, Workflow } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { RowMenu } from '@/components/common/RowMenu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../../shared/format'
import { useConvertOffer } from '../../api'
import type { Offer, OfferStatus } from '../../types'
import { toastError } from '../../ui'
import { FLOW_TONE } from '../../quotes'
import { QuoteFlowModal } from '../quotes/QuoteFlowModal'

const ICON_TONE: Record<OfferStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-info/12 text-info',
  accepted: 'bg-success/12 text-success',
  rejected: 'bg-destructive/10 text-destructive',
}

type Props = { offer: Offer; canWrite: boolean; onEdit: () => void; onDelete: () => void }

/** Teklif kartı: kalemler, toplamlar ve duruma göre hızlı işlemler her zaman görünür. */
export function OfferCard({ offer: o, canWrite, onEdit, onDelete }: Props) {
  const { t } = useTranslation()
  const convert = useConvertOffer()
  const navigate = useNavigate()
  const [flowOpen, setFlowOpen] = useState(false)

  const toOrder = async () => {
    const ok = await confirmModal({ title: t('crm.order.convertTitle', { number: o.number }), description: t('crm.order.convertBody'), confirmLabel: t('crm.order.convert') })
    if (!ok) return
    try {
      await convert.mutateAsync(o.id)
      toast.success(t('crm.order.created'))
      navigate(`/customers/${o.customerId}/orders`)
    } catch (e) {
      toastError(e)
    }
  }


  return (
    <article className="flex min-w-0 flex-col overflow-hidden rounded-xl border bg-card shadow-card transition-shadow hover:shadow-card-hover">
      <header className="flex items-start gap-3 p-4 pb-3">
        <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-lg', ICON_TONE[o.status])}>
          <FileSignature className="size-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-mono text-sm font-semibold">{o.number}</h3>
            <Badge tone={FLOW_TONE[o.flowStatus]}>{t(`crm.quote.status.${o.flowStatus}`)}</Badge>
            <Badge tone="neutral">Rev. {o.revision}</Badge>
            {o.expired ? <Badge tone="warning">{t('crm.offer.expired')}</Badge> : null}
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span>{fmtDate(o.date)}</span>
            <span className={cn('inline-flex items-center gap-1', o.expired && 'text-destructive')}>
              <CalendarClock className="size-3.5" aria-hidden />
              {t('crm.offer.validShort', { date: fmtDate(o.validUntil) })}
            </span>
          </p>
        </div>
        {canWrite ? <RowMenu label={o.number} onEdit={onEdit} onDelete={onDelete} /> : null}
      </header>

      <div className="px-4">
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t('crm.offer.itemCount', { count: o.items.length })}</p>
        <ul className="divide-y rounded-lg border">
          {o.items.map((i) => (
            <li key={i.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{i.name}</p>
                <p className="text-xs tabular-nums text-muted-foreground">
                  {i.qty} × {fmtMoney(i.unitPrice)}
                </p>
              </div>
              <span className="shrink-0 font-medium tabular-nums">{fmtMoney(o.lineTotals[i.id] ?? '0')}</span>
            </li>
          ))}
        </ul>
      </div>

      <dl className="mx-4 mt-3 space-y-1 rounded-lg bg-muted/50 px-3 py-2.5 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">{t('crm.offer.subtotal')}</dt>
          <dd className="tabular-nums">{fmtMoney(o.subtotal)}</dd>
        </div>
        {o.discountPct ? (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">{t('crm.offer.discountShort', { pct: o.discountPct })}</dt>
            <dd className="tabular-nums text-success">−{fmtMoney(o.discount)}</dd>
          </div>
        ) : null}
        <div className="flex items-baseline justify-between gap-3 border-t pt-1.5">
          <dt className="font-medium">{t('crm.offer.total')}</dt>
          <dd className="text-lg font-semibold tabular-nums">{fmtMoney(o.total)}</dd>
        </div>
      </dl>

      {o.note ? (
        <p className="mx-4 mt-3 flex gap-2 text-xs text-muted-foreground">
          <StickyNote className="mt-px size-3.5 shrink-0" aria-hidden />
          <span>{o.note}</span>
        </p>
      ) : null}

      <footer className="mt-auto flex flex-wrap items-center justify-end gap-2 p-4 pt-3">
        {o.status === 'accepted' ? (
          <span className="mr-auto inline-flex items-center gap-1.5 text-xs font-medium text-success">
            <CheckCircle2 className="size-4" aria-hidden />
            {t('crm.offer.acceptedNote')}
          </span>
        ) : null}
        {o.status === 'accepted' && o.orderId ? (
          <Button size="sm" variant="outline" asChild>
            <Link to={`/customers/${o.customerId}/orders`}>
              {t('crm.order.view')}
              <ArrowRight />
            </Link>
          </Button>
        ) : null}
        {canWrite && o.status === 'accepted' && !o.orderId ? (
          <Button size="sm" disabled={convert.isPending} onClick={() => void toOrder()}>
            <ShoppingBag />
            {t('crm.order.convert')}
          </Button>
        ) : null}
        <Button size="sm" variant={o.flowStatus === 'manager_review' || o.flowStatus === 'owner_review' ? 'default' : 'outline'} onClick={() => setFlowOpen(true)}>
          <Workflow />
          {t('crm.quote.openFlow')}
        </Button>
      </footer>
      {flowOpen ? <QuoteFlowModal offer={o} onClose={() => setFlowOpen(false)} /> : null}
    </article>
  )
}
